<script lang="ts">
  import { renderBeads } from '../preview/bead-render';
  import { kitPlacement, sceneFor } from '../preview/scenes';

  /**
   * Preview « vraies perles » + mise en situation à l'échelle réelle.
   *
   * - mode 'beads' : le patron rendu en perles réalistes, canvas plein cadre.
   * - mode 'scene' : le kit incrusté À L'ÉCHELLE dans une scène SVG (bureau /
   *   salon / grand mur selon la largeur physique — cf. preview/scenes.ts).
   *   Le patron est injecté en `<image href={dataUrl}>` : robuste partout,
   *   contrairement à foreignObject (bugs connus Safari).
   *
   * Couleurs de scène : tokens d'échelle (--cream-*, --bordeaux-*, --gold-*,
   * --ink-*) plutôt que les tokens sémantiques — la scène est une illustration
   * « pièce éclairée » stable, elle ne doit pas s'inverser en dark mode.
   */

  type Props = {
    /** RGBA de la grille, 1 perle = 1 pixel (ConvertResult.preview). */
    preview: Uint8ClampedArray;
    /** Largeur de la grille, en perles. */
    width: number;
    /** Hauteur de la grille, en perles. */
    height: number;
    widthCm: number;
    heightCm: number;
    mode: 'beads' | 'scene';
  };

  let { preview, width, height, widthCm, heightCm, mode }: Props = $props();

  /** Résolution du kit incrusté : il occupe < 600 px de scène, 800 suffit. */
  const SCENE_KIT_SIDE = 800;

  let canvasEl: HTMLCanvasElement | undefined = $state();

  // Mode perles : re-rendu réactif à chaque changement de patron.
  $effect(() => {
    if (mode !== 'beads' || !canvasEl) return;
    try {
      renderBeads(canvasEl, preview, width, height);
    } catch (err) {
      console.error('[preview] rendu perles échoué :', err);
    }
  });

  let scene = $derived(sceneFor(widthCm, heightCm));
  let placement = $derived(kitPlacement(scene, widthCm, heightCm));

  // Mode scène : le patron devient une image (PNG base64) incrustée dans le SVG.
  // Ne dépend PAS de `mode` : dérivé « pull », il n'est calculé que lu dans la
  // branche scène du template, et se sert du cache aux bascules perles↔scène
  // (revue V2 : évitait un re-encodage toDataURL à chaque retour en scène).
  let kitUrl = $derived.by(() => {
    if (typeof document === 'undefined') return '';
    try {
      const c = document.createElement('canvas');
      renderBeads(c, preview, width, height, { maxCanvasSide: SCENE_KIT_SIDE });
      return c.toDataURL('image/png');
    } catch (err) {
      console.error('[preview] incrustation du kit échouée :', err);
      return '';
    }
  });

  const cm = (v: number): string => v.toLocaleString('fr-FR');
  let sizeLabel = $derived(`${cm(widthCm)} × ${cm(heightCm)} cm`);
  let ariaLabel = $derived(
    mode === 'beads'
      ? `Aperçu de ton kit en perles, ${sizeLabel}`
      : `Aperçu de ton kit en perles, ${sizeLabel}, ${scene.ariaContext}`,
  );
  let tilt = $derived(
    scene.tiltDeg > 0 ? `perspective(1100px) rotateX(${scene.tiltDeg}deg)` : undefined,
  );
</script>

{#if mode === 'beads'}
  <div class="scene-preview__beads" role="img" aria-label={ariaLabel}>
    <canvas bind:this={canvasEl} class="scene-preview__canvas"></canvas>
  </div>
{:else}
  <div class="scene-preview" style:transform={tilt}>
    <svg
      viewBox={`0 0 ${scene.viewBox.width} ${scene.viewBox.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
    >
      {#if scene.key === 'desk'}
        <!-- Bureau (900×640, 4 px/cm) : mur, sol, bureau 75 cm, lampe 40 cm -->
        <rect x="0" y="0" width="900" height="616" fill="var(--cream-100)" />
        <rect x="0" y="600" width="900" height="16" fill="var(--cream-200)" />
        <rect x="0" y="616" width="900" height="24" fill="var(--cream-300)" />
        <!-- bureau : plateau + ceinture + pieds -->
        <rect x="130" y="316" width="640" height="14" rx="4" fill="var(--gold-800)" />
        <rect x="146" y="330" width="608" height="10" fill="var(--gold-900)" />
        <rect x="150" y="330" width="16" height="286" fill="var(--gold-900)" />
        <rect x="734" y="330" width="16" height="286" fill="var(--gold-900)" />
        <!-- lampe (40 cm : y 156 → 316) -->
        <rect x="217" y="196" width="6" height="112" fill="var(--ink-800)" />
        <rect x="196" y="306" width="48" height="10" rx="4" fill="var(--ink-800)" />
        <path d="M 198 156 L 242 156 L 254 200 L 186 200 Z" fill="var(--gold-500)" />
        <!-- mug (6 cm) -->
        <rect x="690" y="292" width="26" height="24" rx="4" fill="var(--bordeaux-700)" />
        <path d="M 716 298 a 8 8 0 1 1 0 13" fill="none" stroke="var(--bordeaux-700)" stroke-width="4" />
        <text x="220" y="140" text-anchor="middle">{scene.reference.label}</text>
      {:else if scene.key === 'living'}
        <!-- Salon (960×720, 3 px/cm) : mur, sol, canapé 220 cm (x 150→810) -->
        <rect x="0" y="0" width="960" height="674" fill="var(--cream-100)" />
        <rect x="0" y="674" width="960" height="16" fill="var(--cream-200)" />
        <rect x="0" y="690" width="960" height="30" fill="var(--cream-300)" />
        <!-- canapé : dossier, socle, coussins dossier + assise, accoudoirs -->
        <rect x="170" y="450" width="620" height="170" rx="18" fill="var(--bordeaux-800)" />
        <rect x="170" y="616" width="620" height="74" rx="10" fill="var(--bordeaux-900)" />
        <rect x="216" y="474" width="256" height="86" rx="14" fill="var(--bordeaux-700)" />
        <rect x="488" y="474" width="256" height="86" rx="14" fill="var(--bordeaux-700)" />
        <rect x="206" y="560" width="266" height="62" rx="12" fill="var(--bordeaux-600)" />
        <rect x="488" y="560" width="266" height="62" rx="12" fill="var(--bordeaux-600)" />
        <rect x="150" y="520" width="56" height="170" rx="16" fill="var(--bordeaux-700)" />
        <rect x="754" y="520" width="56" height="170" rx="16" fill="var(--bordeaux-700)" />
        <rect
          x="252" y="496" width="62" height="62" rx="14"
          fill="var(--gold-500)" transform="rotate(-8 283 527)"
        />
        <text x="480" y="712" text-anchor="middle">{scene.reference.label}</text>
      {:else}
        <!-- Grand mur (1000×640, 2,2 px/cm) : porte 200 cm, silhouette 170 cm -->
        <rect x="0" y="0" width="1000" height="596" fill="var(--cream-100)" />
        <rect x="0" y="596" width="1000" height="14" fill="var(--cream-200)" />
        <rect x="0" y="610" width="1000" height="30" fill="var(--cream-300)" />
        <!-- porte (200 cm : y 170 → 610) -->
        <rect x="44" y="162" width="196" height="448" fill="var(--cream-400)" />
        <rect x="54" y="170" width="176" height="440" fill="var(--ink-800)" />
        <rect x="70" y="190" width="144" height="180" rx="4" fill="none" stroke="var(--ink-700)" stroke-width="3" />
        <rect x="70" y="390" width="144" height="196" rx="4" fill="none" stroke="var(--ink-700)" stroke-width="3" />
        <circle cx="214" cy="392" r="6" fill="var(--gold-500)" />
        <!-- silhouette (170 cm : y 236 → 610) -->
        <g fill="var(--ink-300)">
          <circle cx="300" cy="256" r="20" />
          <rect x="272" y="282" width="56" height="150" rx="26" />
          <rect x="284" y="420" width="14" height="190" />
          <rect x="302" y="420" width="14" height="190" />
        </g>
        <text x="142" y="630" text-anchor="middle">{scene.reference.label}</text>
      {/if}

      {#if kitUrl !== ''}
        <!-- Le kit à l'échelle : ombre portée, support, patron, liseré -->
        <rect
          x={placement.x + 5}
          y={placement.y + 8}
          width={placement.width}
          height={placement.height}
          rx="2"
          fill="var(--ink-950)"
          opacity="0.16"
        />
        <rect
          x={placement.x}
          y={placement.y}
          width={placement.width}
          height={placement.height}
          fill="var(--cream-50)"
        />
        <image
          href={kitUrl}
          x={placement.x}
          y={placement.y}
          width={placement.width}
          height={placement.height}
          preserveAspectRatio="none"
        />
        <rect
          x={placement.x}
          y={placement.y}
          width={placement.width}
          height={placement.height}
          fill="none"
          stroke="var(--ink-900)"
          stroke-opacity="0.25"
        />
      {/if}
    </svg>

    {#if placement.overflow}
      <span class="badge scene-preview__real-size">Taille réelle : {sizeLabel}</span>
    {/if}
  </div>
{/if}

<style>
  /* Uniquement du positionnement local — les primitives (.badge) sont globales. */

  .scene-preview {
    position: relative;
  }

  .scene-preview svg {
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
  }

  /* Libellés d'échelle : couleur fixe (la scène n'a pas de variante dark). */
  .scene-preview text {
    font-family: var(--font-body);
    font-size: 20px;
    font-weight: 500;
    fill: var(--ink-600);
  }

  .scene-preview__real-size {
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
  }

  .scene-preview__beads {
    display: block;
  }

  .scene-preview__canvas {
    display: block;
    width: 100%;
    height: auto;
  }
</style>
